import { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Tabs, Tab } from "@mui/material";
import {
  processUniqueClients,
  processUberFile,
  processRightRideJsonFile,
  processClaimFiles,
} from "../services/fileProcessingService";
import { toast } from "../components/Toast/Toast";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ProcessFiles = () => {
  const [rightRideFile, setRightRideFile] = useState(null);
  const [nemtFile, setNemtFile] = useState(null);
  const [uberFile, setUberFile] = useState(null);
  const [rightRideJsonFile, setRightRideJsonFile] = useState(null);
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const fileRightRideRef = useRef(null);
  const fileNemtRef = useRef(null);
  const fileUberRef = useRef(null);
  const fileRightRideJsonRef = useRef(null);
  const file1Ref = useRef(null);
  const file2Ref = useRef(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [loadingUnique, setLoadingUnique] = useState(false);
  const [loadingUber, setLoadingUber] = useState(false);
  const [loadingRightRide, setLoadingRightRide] = useState(false);
  const [memoFilter, setMemoFilter] = useState("independent");
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFileChange = (e, setFile, mode) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file, mode)) {
        setFile(file);
      }
    }
  };

  const handleMultipleFileChange = (e, setFile, mode) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const validFiles = files.filter((f) => validateFile(f, mode));
      if (validFiles.length > 0) {
        setFile(validFiles);
      }
    }
  };

  const validateFile = (file, mode) => {
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (mode === "json") {
      const validTypes = ["application/json"];
      const validExtensions = [".json"];
      if (
        !validExtensions.includes(fileExtension) &&
        !validTypes.includes(file.type)
      ) {
        setErrorMsg("Please upload a valid JSON file (.json)");
        setShowError(true);
        return false;
      }
    } else if (mode === "excel") {
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];
      const validExtensions = [".xls", ".xlsx", ".csv"];
      if (
        !validExtensions.includes(fileExtension) &&
        !validTypes.includes(file.type)
      ) {
        setErrorMsg(
          "Please upload a valid Excel or CSV file (.xls, .xlsx, .csv)",
        );
        setShowError(true);
        return false;
      }
    }
    return true;
  };

  const handleUniqueClientFiles = async () => {
    if (!rightRideFile && !nemtFile) {
      setErrorMsg("Please upload at least one file");
      setShowError(true);
      return;
    }

    setLoadingUnique(true);
    try {
      const blob = await processUniqueClients(rightRideFile, nemtFile);

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "rightride-portal-updated.xlsx");

      // Append to the body, click, and cleanup
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Files processed and downloaded successfully");
    } catch (error) {
      console.error("Error processing files:", error);
      setErrorMsg(error.message || "An unexpected error occurred");
      setShowError(true);
    } finally {
      setLoadingUnique(false);
    }
  };

  const handleUberFiles = async () => {
    if (!uberFile) {
      setErrorMsg("Please upload the Uber file");
      setShowError(true);
      return;
    }

    setLoadingUber(true);
    try {
      const data = await processUberFile(uberFile, memoFilter);

      data.files.forEach((file) => {
        const blob = new Blob([file.content], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.name);

        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      });

      toast.success("Files processed and downloaded successfully");
    } catch (error) {
      console.error("Error processing file:", error);
      setErrorMsg(error.message || "An unexpected error occurred");
      setShowError(true);
    } finally {
      setLoadingUber(false);
    }
  };

  const handleRightRideJsonFiles = async () => {
    if (!rightRideJsonFile) {
      setErrorMsg("Please upload a JSON file");
      setShowError(true);
      return;
    }

    setLoadingRightRide(true);
    try {
      const blob = await processRightRideJsonFile(rightRideJsonFile);

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "mercy-care-claims.csv");

      // Append to the body, click, and cleanup
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("File processed and downloaded successfully");
    } catch (error) {
      console.error("Error processing file:", error);
      setErrorMsg(error.message || "An unexpected error occurred");
      setShowError(true);
    } finally {
      setLoadingRightRide(false);
    }
  };

  const handleClaimFiles = async () => {
    if (!file1 && (!file2 || file2.length === 0)) {
      setErrorMsg("Please upload atleast one file");
      setShowError(true);
      return;
    }

    setLoadingUnique(true);
    try {
      const blob = await processClaimFiles(file1, file2);

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "new_mercy-care-claims.csv");

      // Append to the body, click, and cleanup
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Files processed and downloaded successfully");
    } catch (error) {
      console.error("Error processing files:", error);
      setErrorMsg(error.message || "An unexpected error occurred");
      setShowError(true);
    } finally {
      setLoadingUnique(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };
  return (
    <div className="min-h-screen bg-[#f8fafc] py-12 px-6 font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]">
      <div className="text-center mb-16 animate-fadeInDown">
        <h1 className="text-5xl font-extrabold text-[#0f172a] mb-5 tracking-tight">
          Trips & Members Utility Tool
        </h1>
        <p className="text-xl text-[#64748b] m-0 font-medium">
          A professional suite for processing transportation data
        </p>
      </div>

      <div className="max-w-6xl mx-auto mb-10 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
        <Box
          sx={{
            backgroundColor: "#f1f5f9",
            borderRadius: "16px",
            padding: "6px",
            border: "1px solid #e2e8f0",
            boxShadow: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="utility tool tabs"
            variant="fullWidth"
            sx={{
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTabs-flexContainer": {
                gap: "8px",
              },
              "& .MuiTab-root": {
                fontSize: "0.95rem",
                fontWeight: "600",
                textTransform: "none",
                minHeight: "48px",
                borderRadius: "12px",
                color: "#64748b",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  color: "#3182ce",
                  backgroundColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-selected": {
                  color: "#3182ce",
                  backgroundColor: "#ffffff",
                  boxShadow:
                    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                },
              },
            }}
          >
            <Tab label="Unique Clients" {...a11yProps(0)} />
            <Tab label="Uber Trips" {...a11yProps(1)} />
            <Tab label="Right Ride" {...a11yProps(2)} />
            <Tab label="Mercy Claims" {...a11yProps(3)} />
          </Tabs>
        </Box>
      </div>

      <CustomTabPanel value={tabValue} index={0}>
        <div className="max-w-6xl mx-auto mb-6 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
          <h2 className="text-2xl font-bold text-[#1e293b] tracking-tight border-l-4 border-[#3182ce] pl-4">
            Unique Clients Processing
          </h2>
          <p className="text-[#64748b] mt-2 pl-5">
            Compare and extract unique client data from portal exports
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-10 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
          {/* File Upload Zone 1 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#3182ce] rounded-xl flex items-center justify-center shadow-lg shadow-[#3182ce]/20">
                <svg
                  className="w-7 h-7 text-white stroke-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 m-0">
                RightRide Portal
              </h2>
            </div>

            <div
              className={`border-3 border-dashed rounded-2xl py-12 px-8 text-center transition-all duration-300 cursor-pointer relative overflow-hidden active:scale-[0.98] ${rightRideFile ? "border-green-500 bg-green-50/50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
              onClick={() => fileRightRideRef.current?.click()}
            >
              {!rightRideFile ? (
                <>
                  <svg
                    className="w-16 h-16 text-[#3182ce] mx-auto mb-5 stroke-2 animate-float"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-xl font-semibold text-gray-800 mb-2">
                    Click to select file
                  </p>
                  <p className="text-sm text-gray-400 mt-5 font-medium">
                    Supported formats: XLS, XLSX, CSV
                  </p>
                </>
              ) : (
                <div className="w-full flex items-center justify-between p-5 bg-white rounded-xl shadow-md animate-slideIn pointer-events-none overflow-hidden">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <svg
                      className="w-10 h-10 text-green-500 stroke-2 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-base font-semibold text-gray-800 mb-1 truncate">
                        {rightRideFile.name}
                      </p>
                      <p className="text-sm text-gray-500 m-0">
                        {formatFileSize(rightRideFile.size)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 italic">
                        Click to replace
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <input
                ref={fileRightRideRef}
                type="file"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv"
                onChange={(e) => handleFileChange(e, setRightRideFile, "excel")}
                style={{ display: "none" }}
              />
            </div>
          </div>

          {/* File Upload Zone 2 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#3182ce] rounded-xl flex items-center justify-center shadow-lg shadow-[#3182ce]/20">
                <svg
                  className="w-7 h-7 text-white stroke-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 m-0">
                NEMT Portal
              </h2>
            </div>

            <div
              className={`border-3 border-dashed rounded-2xl py-12 px-8 text-center transition-all duration-300 cursor-pointer relative overflow-hidden active:scale-[0.98] ${nemtFile ? "border-green-500 bg-green-50/50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
              onClick={() => fileNemtRef.current?.click()}
            >
              {!nemtFile ? (
                <>
                  <svg
                    className="w-16 h-16 text-[#3182ce] mx-auto mb-5 stroke-2 animate-float"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-xl font-semibold text-gray-800 mb-2">
                    Click to select file
                  </p>
                  <p className="text-sm text-gray-400 mt-5 font-medium">
                    Supported formats: XLS, XLSX, CSV
                  </p>
                </>
              ) : (
                <div className="w-full flex items-center justify-between p-5 bg-white rounded-xl shadow-md animate-slideIn pointer-events-none overflow-hidden">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <svg
                      className="w-10 h-10 text-green-500 stroke-2 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-base font-semibold text-gray-800 mb-1 truncate">
                        {nemtFile.name}
                      </p>
                      <p className="text-sm text-gray-500 m-0">
                        {formatFileSize(nemtFile.size)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 italic">
                        Click to replace
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <input
                ref={fileNemtRef}
                type="file"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv"
                onChange={(e) => handleFileChange(e, setNemtFile, "excel")}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-center mb-12">
          <button
            onClick={handleUniqueClientFiles}
            disabled={loadingUnique || (!rightRideFile && !nemtFile)}
            className={`
            px-10 py-4 rounded-xl text-xl font-bold transition-all duration-300 shadow-xl
            ${
              loadingUnique || (!rightRideFile && !nemtFile)
                ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                : "bg-gradient-to-r from-[#3182ce] to-[#2c5282] text-white hover:scale-105 active:scale-95 hover:shadow-2xl hover:shadow-[#3182ce]/40"
            }
          `}
          >
            {loadingUnique ? (
              <div className="flex items-center gap-3">
                <svg
                  className="animate-spin h-6 w-6 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </div>
            ) : (
              "Process"
            )}
          </button>
        </div>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        {/* Uber Trips */}
        <div className="max-w-6xl mx-auto mb-6 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
          <h2 className="text-2xl font-bold text-[#1e293b] tracking-tight border-l-4 border-[#3182ce] pl-4">
            Uber Trips Analysis
          </h2>
          <p className="text-[#64748b] mt-2 pl-5">
            Process and filter Uber trip data with memo markers
          </p>
        </div>
        <div className="max-w-6xl mx-auto mb-10 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
          {/* File Upload Zone 1 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#3182ce] rounded-xl flex items-center justify-center shadow-lg shadow-[#3182ce]/20">
                  <svg
                    className="w-7 h-7 text-white stroke-2"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 m-0">
                  Uber Trips File
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">
                  Filter:
                </label>
                <select
                  value={memoFilter}
                  onChange={(e) => setMemoFilter(e.target.value)}
                  className="border border-gray-200 rounded-xl px-3 py-1 text-sm bg-gray-50 shadow-sm focus:ring-2 focus:ring-[#3182ce] focus:border-transparent outline-none transition-all"
                >
                  {/* <option value="All">All</option> */}
                  <option value="independent">Independent</option>
                  {/* <option value="rightRide">RightRide</option> */}
                  {/* <option value="NEMT">NEMT</option> */}
                </select>
              </div>
            </div>

            <div
              className={`border-3 border-dashed rounded-2xl py-12 px-8 text-center transition-all duration-300 cursor-pointer relative overflow-hidden active:scale-[0.98] ${uberFile ? "border-green-500 bg-green-50/50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
              onClick={() => fileUberRef.current?.click()}
            >
              {!uberFile ? (
                <>
                  <svg
                    className="w-16 h-16 text-[#3182ce] mx-auto mb-5 stroke-2 animate-float"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-xl font-semibold text-gray-800 mb-2">
                    Click to select file
                  </p>
                  <p className="text-sm text-gray-400 mt-5 font-medium">
                    Supported formats: XLS, XLSX, CSV
                  </p>
                </>
              ) : (
                <div className="w-full flex items-center justify-between p-5 bg-white rounded-xl shadow-md animate-slideIn pointer-events-none overflow-hidden">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <svg
                      className="w-10 h-10 text-green-500 stroke-2 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-base font-semibold text-gray-800 mb-1 truncate">
                        {uberFile.name}
                      </p>
                      <p className="text-sm text-gray-500 m-0">
                        {formatFileSize(uberFile.size)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 italic">
                        Click to replace
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <input
                ref={fileUberRef}
                type="file"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv"
                onChange={(e) => handleFileChange(e, setUberFile, "excel")}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="max-w-6xl mx-auto flex justify-end mb-12">
          <button
            onClick={handleUberFiles}
            disabled={loadingUber || !uberFile}
            className={`
            px-10 py-4 rounded-xl text-xl font-bold transition-all duration-300 shadow-xl
            ${
              loadingUber || !uberFile
                ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                : "bg-gradient-to-r from-[#3182ce] to-[#2c5282] text-white hover:scale-105 active:scale-95 hover:shadow-2xl hover:shadow-[#3182ce]/40"
            }
          `}
          >
            {loadingUber ? (
              <div className="flex items-center gap-3">
                <svg
                  className="animate-spin h-6 w-6 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </div>
            ) : (
              "Process"
            )}
          </button>
        </div>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={2}>
        {/* JSON Files */}
        <div className="max-w-6xl mx-auto mb-6 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
          <h2 className="text-2xl font-bold text-[#1e293b] tracking-tight border-l-4 border-[#3182ce] pl-4">
            Right Ride JSON Utility
          </h2>
          <p className="text-[#64748b] mt-2 pl-5">
            Convert Right Ride JSON data to CSV format
          </p>
        </div>
        <div className="max-w-6xl mx-auto mb-10 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
          {/* File Upload Zone 1 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#3182ce] rounded-xl flex items-center justify-center shadow-lg shadow-[#3182ce]/20">
                <svg
                  className="w-7 h-7 text-white stroke-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 m-0">
                RightRide Json File
              </h2>
            </div>

            <div
              className={`border-3 border-dashed rounded-2xl py-12 px-8 text-center transition-all duration-300 cursor-pointer relative overflow-hidden active:scale-[0.98] ${rightRideJsonFile ? "border-green-500 bg-green-50/50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
              onClick={() => fileRightRideJsonRef.current?.click()}
            >
              {!rightRideJsonFile ? (
                <>
                  <svg
                    className="w-16 h-16 text-[#3182ce] mx-auto mb-5 stroke-2 animate-float"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-xl font-semibold text-gray-800 mb-2">
                    Click to select file
                  </p>
                  <p className="text-sm text-gray-400 mt-5 font-medium">
                    Supported formats: JSON
                  </p>
                </>
              ) : (
                <div className="w-full flex items-center justify-between p-5 bg-white rounded-xl shadow-md animate-slideIn pointer-events-none overflow-hidden">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <svg
                      className="w-10 h-10 text-green-500 stroke-2 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-base font-semibold text-gray-800 mb-1 truncate">
                        {rightRideJsonFile.name}
                      </p>
                      <p className="text-sm text-gray-500 m-0">
                        {formatFileSize(rightRideJsonFile.size)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 italic">
                        Click to replace
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <input
                ref={fileRightRideJsonRef}
                type="file"
                accept=".json"
                onChange={(e) =>
                  handleFileChange(e, setRightRideJsonFile, "json")
                }
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="max-w-6xl mx-auto flex justify-end mb-12">
          <button
            onClick={handleRightRideJsonFiles}
            disabled={loadingRightRide || !rightRideJsonFile}
            className={`
            px-10 py-4 rounded-xl text-xl font-bold transition-all duration-300 shadow-xl
            ${
              loadingRightRide || !rightRideJsonFile
                ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                : "bg-gradient-to-r from-[#3182ce] to-[#2c5282] text-white hover:scale-105 active:scale-95 hover:shadow-2xl hover:shadow-[#3182ce]/40"
            }
          `}
          >
            {loadingRightRide ? (
              <div className="flex items-center gap-3">
                <svg
                  className="animate-spin h-6 w-6 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </div>
            ) : (
              "Process"
            )}
          </button>
        </div>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={3}>
        <div className="max-w-6xl mx-auto mb-6 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
          <h2 className="text-2xl font-bold text-[#1e293b] tracking-tight border-l-4 border-[#3182ce] pl-4">
            Mercy Claims Generator
          </h2>
          <p className="text-[#64748b] mt-2 pl-5">
            Generate claim files from source and list comparisons
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-10 animate-fadeInUp fill-mode-both [animation-delay:200ms]">
          {/* File Upload Zone 1 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#3182ce] rounded-xl flex items-center justify-center shadow-lg shadow-[#3182ce]/20">
                <svg
                  className="w-7 h-7 text-white stroke-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 m-0">
                Source File (Single)
              </h2>
            </div>

            <div
              className={`border-3 border-dashed rounded-2xl py-12 px-8 text-center transition-all duration-300 cursor-pointer relative overflow-hidden active:scale-[0.98] ${file1 ? "border-green-500 bg-green-50/50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
              onClick={() => file1Ref.current?.click()}
            >
              {!file1 ? (
                <>
                  <svg
                    className="w-16 h-16 text-[#3182ce] mx-auto mb-5 stroke-2 animate-float"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-xl font-semibold text-gray-800 mb-2">
                    Click to select file
                  </p>
                  <p className="text-sm text-gray-400 mt-5 font-medium">
                    Supported formats: XLS, XLSX, CSV
                  </p>
                </>
              ) : (
                <div className="w-full flex items-center justify-between p-5 bg-white rounded-xl shadow-md animate-slideIn pointer-events-none overflow-hidden">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <svg
                      className="w-10 h-10 text-green-500 stroke-2 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-base font-semibold text-gray-800 mb-1 truncate">
                        {file1.name}
                      </p>
                      <p className="text-sm text-gray-500 m-0">
                        {formatFileSize(file1.size)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 italic">
                        Click to replace
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <input
                ref={file1Ref}
                type="file"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv"
                onChange={(e) => handleFileChange(e, setFile1, "excel")}
                style={{ display: "none" }}
              />
            </div>
          </div>

          {/* File Upload Zone 2 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#3182ce] rounded-xl flex items-center justify-center shadow-lg shadow-[#3182ce]/20">
                <svg
                  className="w-7 h-7 text-white stroke-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 m-0">
                ClaimList Files (Multiple)
              </h2>
            </div>

            <div
              className={`border-3 border-dashed rounded-2xl py-12 px-8 text-center transition-all duration-300 cursor-pointer relative overflow-hidden active:scale-[0.98] ${file2 && file2.length > 0 ? "border-green-500 bg-green-50/50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
              onClick={() => file2Ref.current?.click()}
            >
              {!file2 || file2.length === 0 ? (
                <>
                  <svg
                    className="w-16 h-16 text-[#3182ce] mx-auto mb-5 stroke-2 animate-float"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-xl font-semibold text-gray-800 mb-2">
                    Click to select file
                  </p>
                  <p className="text-sm text-gray-400 mt-5 font-medium">
                    Supported formats: XLS, XLSX, CSV
                  </p>
                </>
              ) : (
                <div className="w-full flex flex-col gap-3 p-5 bg-white rounded-xl shadow-md animate-slideIn pointer-events-none max-h-48 overflow-y-auto">
                  <div className="flex items-center justify-between border-b pb-2 mb-2">
                    <span className="font-semibold text-gray-700">
                      {file2.length} File(s) Selected
                    </span>
                  </div>
                  {file2.map((f, i) => (
                    <div key={i} className="flex items-center gap-4 w-full">
                      <svg
                        className="w-8 h-8 text-green-500 stroke-2 shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-semibold text-gray-800 mb-1 truncate">
                          {f.name}
                        </p>
                        <p className="text-xs text-gray-500 m-0">
                          {formatFileSize(f.size)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400 mt-2 italic text-center w-full">
                    Click to replace all
                  </p>
                </div>
              )}
              <input
                ref={file2Ref}
                type="file"
                multiple
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv"
                onChange={(e) => handleMultipleFileChange(e, setFile2, "excel")}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-center mb-12">
          <button
            onClick={handleClaimFiles}
            disabled={
              loadingUnique || (!file1 && (!file2 || file2.length === 0))
            }
            className={`
            px-10 py-4 rounded-xl text-xl font-bold transition-all duration-300 shadow-xl
            ${
              loadingUnique || (!file1 && (!file2 || file2.length === 0))
                ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                : "bg-gradient-to-r from-[#3182ce] to-[#2c5282] text-white hover:scale-105 active:scale-95 hover:shadow-2xl hover:shadow-[#3182ce]/40"
            }
          `}
          >
            {loadingUnique ? (
              <div className="flex items-center gap-3">
                <svg
                  className="animate-spin h-6 w-6 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </div>
            ) : (
              "Process"
            )}
          </button>
        </div>
      </CustomTabPanel>

      {/* Error Popup */}
      {showError && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn p-5"
          onClick={() => setShowError(false)}
        >
          <div
            className="bg-white rounded-[20px] shadow-2xl max-w-sm w-full animate-scaleIn overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between py-4 px-6 border-b border-[#EF5350]/20 bg-white">
              <h2 className="text-xl font-bold text-gray-800 m-0">Error</h2>
              <button
                className="w-8 h-8 bg-transparent border-none rounded-lg cursor-pointer flex items-center justify-center transition-all duration-200 p-0 hover:bg-gray-100"
                onClick={() => setShowError(false)}
              >
                <svg
                  className="w-5 h-5 text-gray-500 stroke-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-8 flex flex-col gap-6 text-center">
              <div className="flex items-center justify-center gap-4">
                {/* Icon Container */}
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shadow-md shrink-0"
                  style={{ backgroundColor: "#EF5350" }}
                >
                  <svg
                    className="w-6 h-6 text-white stroke-2"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>

                {/* Text Container */}
                <div className="text-left">
                  <p className="text-gray-900 text-lg font-bold leading-tight m-0">
                    Please upload both files
                  </p>
                </div>
              </div>
              <p className="text-gray-600 font-medium">{errorMsg}</p>
              <button
                className="w-full py-3 px-6 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
                style={{
                  backgroundColor: "#EF5350",
                  boxShadow: "0 10px 15px -3px rgba(239, 83, 80, 0.3)",
                }}
                onClick={() => setShowError(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessFiles;
